import { NgTemplateOutlet } from '@angular/common';
import { Component, computed, inject, input } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';

import { UserSelectContainerComponent } from '@core/account-ui';
import { NgTemplateAssertType, TimeService } from '@core/common-legacy';
import {
  BooleanFieldControls,
  CustomFieldFormGroup,
  CustomFieldType,
  DateFieldControls,
  MoneyFieldControls,
  MultiChoiceFieldControls,
  MultiUserFieldControls,
  NumberFieldControls,
  SingleChoiceFieldControls,
  SingleLineFieldControls,
  SingleUserFieldControls,
} from '@core/custom-fields-types';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import {
  BadgeComponent,
  ButtonComponent,
  CheckboxComponent,
  ConnectedOverlayDirective,
  DatePickerComponent,
  DropdownComponent,
  DropdownHeaderComponent,
  FormFieldComponent,
  IconComponent,
  IconHelpComponent,
  InputComponent,
  LabelComponent,
  NumericInputComponent,
  OverlayOriginDirective,
  ProtectedControlDirective,
  RtTooltip,
  SelectComponent,
  SelectOption,
  SelectOptionValue,
} from '@core/user-interface-legacy';
import { CurrencySelectFieldComponent } from '@recruitee/currency-ui';

import { MultiUserCustomFieldContainerComponent } from '../containers/multi-user-custom-field-container/multi-user-custom-field-container.component';
import { MultiSelectCustomFieldComponent } from '../multi-select-custom-field/multi-select-custom-field.component';

export enum CustomFieldViewContext {
  Requisition = 'requisition',
  Job = 'job',
}

@Component({
  selector: 'rt-custom-field',
  imports: [
    FormFieldComponent,
    LabelComponent,
    InputComponent,
    ReactiveFormsModule,
    ProtectedControlDirective,
    SelectComponent,
    TranslatePipe,
    NgTemplateAssertType,
    NgTemplateOutlet,
    CheckboxComponent,
    NumericInputComponent,
    CurrencySelectFieldComponent,
    DatePickerComponent,
    IconComponent,
    DropdownComponent,
    DropdownHeaderComponent,
    OverlayOriginDirective,
    ConnectedOverlayDirective,
    ButtonComponent,
    MultiSelectCustomFieldComponent,
    MultiUserCustomFieldContainerComponent,
    RtTooltip,
    IconHelpComponent,
    UserSelectContainerComponent,
    RtTooltip,
    BadgeComponent,
  ],
  templateUrl: './custom-field.component.html',
  styleUrl: './custom-field.component.less',
})
export class CustomFieldComponent {
  public readonly i18n = inject(I18nService);
  public readonly timeService = inject(TimeService);

  public readonly CustomFieldType = CustomFieldType;
  public readonly CustomFieldViewContext = CustomFieldViewContext;
  public readonly translations = {
    yes: this.i18n.translate('app.common.boolean.yes'),
    no: this.i18n.translate('app.common.boolean.no'),
    min: this.i18n.translate('app.common.min'),
    max: this.i18n.translate('app.common.max'),
    iconHelpTooltip: {
      [CustomFieldViewContext.Job]: this.i18n.translate('app.offer_form.multi_user_field_tooltip'),
      [CustomFieldViewContext.Requisition]: this.i18n.translate(
        'app.requisitions.form_fields.multi_user_field_tooltip',
      ),
    },
  };
  public readonly singleLineContextType: {
    customFieldFormGroup: FormGroup<SingleLineFieldControls>;
  };
  public readonly singleChoiceContextType: {
    customFieldFormGroup: FormGroup<SingleChoiceFieldControls>;
  };
  public readonly multiChoiceContextType: {
    customFieldFormGroup: FormGroup<MultiChoiceFieldControls>;
  };
  public readonly booleanContextType: {
    customFieldFormGroup: FormGroup<BooleanFieldControls>;
  };
  public readonly numberContextType: {
    customFieldFormGroup: FormGroup<NumberFieldControls>;
  };
  public readonly moneyContextType: {
    customFieldFormGroup: FormGroup<MoneyFieldControls>;
  };
  public readonly dateContextType: {
    customFieldFormGroup: FormGroup<DateFieldControls>;
  };
  public readonly singleUserContextType: {
    customFieldFormGroup: FormGroup<SingleUserFieldControls>;
  };
  public readonly multiUserContextType: {
    customFieldFormGroup: FormGroup<MultiUserFieldControls>;
  };

  public customFieldFormGroup = input.required<CustomFieldFormGroup>();
  public isReadOnly = input<boolean>(false);
  public hideRequiredBadges = input.required<boolean>();
  public errorMessagesVisible = input<boolean>(true);
  public isTranslationMarked = input<boolean>(false);
  public isPrimaryLang = input<boolean>(true);
  public customFieldsViewContext = input<CustomFieldViewContext>(CustomFieldViewContext.Job);

  public selectOptions = computed<SelectOption[]>(
    () =>
      this.customFieldFormGroup().value.configuration?.options?.map(option => ({
        label: option.value,
        value: option.id,
      })) || [],
  );

  public get iconHelpTooltip(): string {
    return this.translations.iconHelpTooltip[this.customFieldsViewContext()];
  }

  public getSelectedDate(value: string | null): string | null {
    if (!value) return null;
    return this.timeService.format({ input: value, pattern: 'YYYY-MM-DD' });
  }

  public clearDate() {
    (<FormGroup<DateFieldControls>>(
      this.customFieldFormGroup()
    )).controls.value.controls.value.setValue(null);
  }

  public setSingleUserValue(value: SelectOptionValue) {
    const control = (<FormGroup<SingleUserFieldControls>>this.customFieldFormGroup()).controls.value
      .controls.admin;
    control.setValue(<number>value);
  }

  protected hasControlError(control: FormControl): boolean {
    return !control.valid ? control.touched : false;
  }
}
